// Enums from docs/SCHEMA.dbml. The `as const` tuples are the single source of
// truth: Zod schemas here and Drizzle `pgEnum`s in packages/db both consume them.
import { z } from 'zod';

export const ROLE_KINDS = ['company', 'curated'] as const;
export type RoleKind = (typeof ROLE_KINDS)[number];

export const ROLE_STATUSES = [
  'draft',
  'open',
  'matching',
  'shortlisted',
  'introduced',
  'closed',
] as const;
export const roleStatusSchema = z.enum(ROLE_STATUSES);
export type RoleStatus = z.infer<typeof roleStatusSchema>;
// Roles that count as live in curator counts: published and not yet closed.
export const LIVE_ROLE_STATUSES: readonly RoleStatus[] = [
  'open',
  'matching',
  'shortlisted',
  'introduced',
];
// Live roles with nobody shortlisted yet: curators are still reviewing the pool.
export const ROLE_AWAITING_SHORTLIST_STATUSES: readonly RoleStatus[] = ['open', 'matching'];
// Any role not yet closed can be closed, by its company or a curator. Company
// roles are usually past `open` (matching/shortlisted/introduced) by then.
// Closing is final: matching stops and no more introductions go out.
export function canCloseRole(status: RoleStatus): boolean {
  return status !== 'closed';
}

export const CANDIDATE_STATUSES = [
  'new',
  'screened',
  'active',
  'paused',
  'placed',
  'withdrawn',
] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];

export const COMPANY_STATUSES = ['pending', 'active', 'suspended'] as const;

export const EMPLOYMENT_TYPES = ['full_time_employee', 'contractor', 'part_time'] as const;
export const employmentTypeSchema = z.enum(EMPLOYMENT_TYPES);
export type EmploymentType = z.infer<typeof employmentTypeSchema>;

export const SENIORITIES = ['junior', 'mid', 'senior', 'lead', 'principal'] as const;
export const senioritySchema = z.enum(SENIORITIES);
export type Seniority = z.infer<typeof senioritySchema>;

export const INDUSTRY_RELATIONS = ['worked_in', 'interested_in'] as const;

export const CURATOR_DECISIONS = ['pending', 'confirmed', 'rejected'] as const;
export type CuratorDecision = (typeof CURATOR_DECISIONS)[number];
/** Most match ids one bulk-decision request accepts; the curator UI batches above it. */
export const BULK_DECISION_MAX = 200;

export const CONSENT_STATES = ['pending', 'yes', 'no'] as const;

export const COMPANY_REACTIONS = ['pending', 'interested', 'pass'] as const;

// A role type a company or candidate added themselves is usable by them at
// once (`pending`); a curator then approves it, replaces it with an existing
// type (links move, row kept as `replaced`) or rejects it (Q77).
export const ROLE_TYPE_STATUSES = ['approved', 'pending', 'rejected', 'replaced'] as const;
export type RoleTypeStatus = (typeof ROLE_TYPE_STATUSES)[number];

// Skills a company user or candidate adds when theirs is missing follow the
// same life cycle (Q79): `pending` and usable at once by its author and anyone
// who adds the same name, then a curator approves it, replaces it with an
// existing skill (links move) or rejects it (links are removed).
export const SKILL_STATUSES = ['approved', 'pending', 'rejected', 'replaced'] as const;
export type SkillStatus = (typeof SKILL_STATUSES)[number];

// Order is display order. The first five predate non-engineering roles; the
// rest mirror the role-type families so any field has a home for its skills.
export const SKILL_CATEGORIES = [
  'design',
  'product',
  'data',
  'language',
  'framework',
  'tool',
  'domain',
  'support',
  'sales',
  'marketing',
  'operations',
  'finance',
  'healthcare',
  'education',
  'soft',
] as const;
export type SkillCategory = (typeof SKILL_CATEGORIES)[number];
